import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, Loader2, ArrowLeft, Mail, CheckCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { settings } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success("Controlla la tua email!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-12 w-auto object-contain" />
            ) : (
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900">{settings.nome_azienda || "Gestione Ferie"}</h2>
              <p className="text-sm text-slate-500">Gestione Ferie e Permessi</p>
            </div>
          </div>

          {!emailSent ? (
            <>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Recupera Password</h1>
                <p className="text-slate-600">
                  Inserisci la tua email e ti invieremo un link per reimpostare la password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@azienda.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="send-reset-email-btn"
                  className="w-full h-12 btn-theme rounded-md btn-press font-medium gap-2"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio in corso...</>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Invia Link di Reset
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-emerald-600" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Email Inviata!</h1>
                <p className="text-slate-600">
                  Abbiamo inviato un link di reset a <strong>{email}</strong>
                </p>
                <p className="text-sm text-slate-500">
                  Controlla la tua casella di posta (anche nello spam). Il link scadrà tra 1 ora.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Non hai ricevuto l'email?</strong><br />
                  Controlla la cartella spam o clicca il pulsante sotto per inviare nuovamente.
                </p>
              </div>

              <Button
                onClick={() => setEmailSent(false)}
                variant="outline"
                className="w-full h-12"
                data-testid="resend-email-btn"
              >
                Invia di nuovo
              </Button>
            </div>
          )}

          <div className="pt-4">
            <Link 
              to="/login" 
              className="inline-flex items-center text-slate-600 hover:text-slate-900"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Torna al Login
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
